import React from 'react';
import { Controller, Control, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { TextField, MenuItem, Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ValidationPatterns } from './helpers';
import { Asset } from '../items/interfaces/IAssets';

// Base interface for all controller props
interface BaseControllerProps<T extends FieldValues> {
    name: Path<T>;
    control: Control<T>;
    rules?: RegisterOptions<T>;
    label: string;
    margin?: 'dense' | 'normal' | 'none';
    fullWidth?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
}

// Text field controller props
interface TextControllerProps<T extends FieldValues> extends BaseControllerProps<T> {
    type?: 'text' | 'email' | 'number' | 'password';
    placeholder?: string;
    multiline?: boolean;
    rows?: number;
    sx?: object;
}

// Select field controller props
interface SelectControllerProps<T extends FieldValues> extends BaseControllerProps<T> {
    options: Array<{ value: string | number; label: string }>;
    native?: boolean;
}

// Date picker controller props
interface DateControllerProps<T extends FieldValues> extends BaseControllerProps<T> {
    // Additional date picker specific props can be added here
}

// Asset autocomplete controller props
interface AssetControllerProps<T extends FieldValues> extends BaseControllerProps<T> {
    options: Asset[];
    required?: boolean;
    sx?: object;
}


/**
 * TextController
 * Reusable Text Field Controller.
 * Wraps Material-UI TextField with react-hook-form Controller.
 * @template T FieldValues type
 * @param {TextControllerProps<T>} props - Props for TextController.
 * @returns {JSX.Element} Controlled text field.
 */
export function TextController<T extends FieldValues>({
    name,
    control,
    rules,
    label,
    type = 'text',
    margin = 'dense',
    fullWidth = true,
    autoFocus = false,
    disabled = false,
    placeholder,
    multiline = false,
    rows,
    sx,
}: TextControllerProps<T>) {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
                <TextField
                    {...field}
                    label={label}
                    type={type}
                    margin={margin}
                    fullWidth={fullWidth}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    placeholder={placeholder}
                    multiline={multiline}
                    rows={multiline ? rows : undefined}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    sx={sx}
                />
            )}
        />
    );
}

/**
 * Reusable Select Field Controller
 * Wraps Material-UI TextField with select functionality and react-hook-form Controller
 */
export function SelectController<T extends FieldValues>({
    name,
    control,
    rules,
    label,
    options,
    margin = 'dense',
    fullWidth = true,
    disabled = false,
    native = false,
}: SelectControllerProps<T>) {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
                <TextField
                    {...field}
                    select
                    label={label}
                    margin={margin}
                    fullWidth={fullWidth}
                    disabled={disabled}
                    SelectProps={{ native }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                >
                    {options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            )}
        />
    );
}

/**
 * Reusable Date Picker Controller
 * Wraps Material-UI DatePicker with react-hook-form Controller
 */
export function DateController<T extends FieldValues>({
    name,
    control,
    rules,
    label,
    margin = 'dense',
    fullWidth = true,
    disabled = false,
}: DateControllerProps<T>) {
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field, fieldState }) => (
                    <DatePicker
                        {...field}
                        label={label}
                        disabled={disabled}
                        slotProps={{
                            textField: {
                                margin,
                                fullWidth,
                                error: !!fieldState.error,
                                helperText: fieldState.error?.message,
                            },
                        }}
                    />
                )}
            />
        </LocalizationProvider>
    );
}

/**
 * Reusable Number Field Controller
 * Specialized version of TextController for numeric inputs with additional validation
 */
export function NumberController<T extends FieldValues>({
    name,
    control,
    rules,
    label,
    margin = 'dense',
    fullWidth = true,
    autoFocus = false,
    disabled = false,
    placeholder,
    sx,
}: Omit<TextControllerProps<T>, 'type'>) {
    return (
        <TextController
            name={name}
            control={control}
            rules={rules}
            label={label}
            type='number'
            margin={margin}
            fullWidth={fullWidth}
            autoFocus={autoFocus}
            disabled={disabled}
            placeholder={placeholder}
            sx={sx}
        />
    );
}

/**
 * Reusable Email Field Controller
 * Specialized version of TextController for email inputs with built-in validation
 */
export function EmailController<T extends FieldValues>({
    name,
    control,
    rules,
    label,
    margin = 'dense',
    fullWidth = true,
    autoFocus = false,
    disabled = false,
    placeholder = 'Enter email address',
    sx,
}: Omit<TextControllerProps<T>, 'type'>) {
    const emailRules: RegisterOptions<T> = {
        ...rules,
        pattern: ValidationPatterns.email,
    } as RegisterOptions<T>;

    return (
        <TextController
            name={name}
            control={control}
            rules={emailRules}
            label={label}
            type='email'
            margin={margin}
            fullWidth={fullWidth}
            autoFocus={autoFocus}
            disabled={disabled}
            placeholder={placeholder}
            sx={sx}
        />
    );
}

/**
 * Reusable Asset Autocomplete Controller
 * Wraps Material-UI Autocomplete with react-hook-form Controller for asset selection
 */
export function AssetController<T extends FieldValues>({
    name,
    control,
    rules,
    label,
    margin = 'dense',
    fullWidth = true,
    autoFocus = false,
    disabled = false,
    options,
    required = false,
    sx,
}: AssetControllerProps<T>) {
    // Add minLength validation to rules
    const validationRules: RegisterOptions<T> = {
        ...rules,
    } as RegisterOptions<T>;

    return (
        <Controller
            name={name}
            control={control}
            rules={validationRules}
            render={({ field, fieldState }) => (
                <Autocomplete
                    options={options}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.asset)}
                    value={options.find((a) => a.asset === field.value) || (typeof field.value === 'string' && field.value !== '' ? field.value : null)}
                    onChange={(_, newValue) => {
                        if (newValue) {
                            if (typeof newValue === 'string') {
                                field.onChange(newValue);
                            } else {
                                field.onChange(newValue.asset);
                            }
                        } else {
                            field.onChange('');
                        }
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                        // Only update on user input (not reset/clear)
                        if (reason === 'input') {
                            field.onChange(newInputValue);
                        }
                    }}
                    disabled={disabled}
                    freeSolo
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={label}
                            variant='outlined'
                            margin={margin}
                            fullWidth={fullWidth}
                            required={required}
                            autoFocus={autoFocus}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            sx={sx}
                            inputProps={{
                                ...params.inputProps,
                                minLength: 5,
                            }}
                        />
                    )}
                />
            )}
        />
    );
}
